import { X } from 'lucide-react'
import { KeyboardEvent, useState } from 'react'

// Kiểm tra gọn phía FE; backend vẫn kiểm tra lại (esign-mail-invalid-address)
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const SEPARATORS = /[\s,;]+/

export const isValidEmail = (value: string) => EMAIL_PATTERN.test(value)

interface EmailTagInputProps {
    value: string[]
    onChange: (value: string[]) => void
    placeholder?: string
    disabled?: boolean
}

/**
 * Ô nhập nhiều email dạng tag. Enter / dấu phẩy / chấm phẩy / khoảng trắng / rời ô
 * để thêm; dán chuỗi nhiều email cũng tự tách. Backspace khi ô trống xoá tag cuối.
 * Email sai định dạng vẫn giữ nhưng tô đỏ để người dùng sửa.
 */
const EmailTagInput = ({
    value,
    onChange,
    placeholder,
    disabled,
}: EmailTagInputProps) => {
    const [draft, setDraft] = useState('')

    const commit = (text: string) => {
        const emails = text
            .split(SEPARATORS)
            .map((email) => email.trim())
            .filter(Boolean)
            .filter((email) => !value.includes(email))
        if (emails.length) onChange([...value, ...emails])
        setDraft('')
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (['Enter', ',', ';', ' ', 'Tab'].includes(event.key)) {
            if (!draft.trim()) return
            event.preventDefault()
            commit(draft)
        } else if (event.key === 'Backspace' && !draft && value.length) {
            onChange(value.slice(0, -1))
        }
    }

    return (
        <div
            className={`flex min-h-[38px] flex-wrap items-center gap-1 rounded-md border border-gray-300 px-2 py-1 focus-within:border-primary ${
                disabled ? 'bg-gray-50' : 'bg-white'
            }`}
        >
            {value.map((email) => (
                <span
                    key={email}
                    className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs ${
                        isValidEmail(email)
                            ? 'bg-gray-100 text-gray-700'
                            : 'bg-red-50 text-red-700 ring-1 ring-red-300'
                    }`}
                >
                    {email}
                    {!disabled && (
                        <button
                            type="button"
                            aria-label={`remove ${email}`}
                            onClick={() =>
                                onChange(value.filter((item) => item !== email))
                            }
                        >
                            <X className="h-3 w-3" />
                        </button>
                    )}
                </span>
            ))}
            <input
                value={draft}
                disabled={disabled}
                placeholder={value.length ? '' : placeholder}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={() => draft.trim() && commit(draft)}
                onPaste={(event) => {
                    const text = event.clipboardData.getData('text')
                    if (SEPARATORS.test(text.trim())) {
                        event.preventDefault()
                        commit(text)
                    }
                }}
                className="min-w-[160px] flex-1 border-0 bg-transparent py-1 text-sm outline-none"
            />
        </div>
    )
}

export default EmailTagInput
